import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import packageJson from '../../../../../package.json'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
} from '../../../../components/mui/Core/index'
import { useTheme } from '../../../../components/mui/Styles/index'
import FormHeading from '../../../../components/userauthentication/FormHeading'
import GenericSnackbar from '../../../../components/GenericSnackbar/generic-snackbar'
import Constants from '../../../../constants/en.json'
import { verifyMfaTokenForLogin } from '../../../../api/user/verify-mfa-for-login'
import { setSnackbar } from '../../../../components/GenericSnackbar/generic-snackbar-slice'
import { transformAuthenticatedUserData } from '../../../../utils/aws-cognito-utils'
import { setAuthenticatedUser } from '../LogIn/log-in-slice'
import { persistor } from '../../../../app/store'
import { getTableauCredentials } from '../../../../api/reporting/tableau/tableau-get-credentials'
import { createTableauUser } from '../../../../api/reporting/tableau/create-tableau-user'
import { addTableauUserToGroup } from '../../../../api/reporting/tableau/add-tableau-user-to-group'
import { tableauUsers } from '../../../../constants/tableau-api-constants'
import { routePaths } from '../../../../constants/routes'
import { errorCodes } from '../../../../constants/error-codes'
import { localStorageKeyConstants } from '../../../../constants/local-storage-key-constants'
import { sessionStorageKeyConstants } from '../../../../constants/session-storage-key-constants'
import { loginChallengeTypes } from '../../../../constants/challenge-type-constants'

function EnterMFA() {
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const mfaData = location.state
  const [totpCode, setTotpCode] = useState('')
  const [isAuthenticatingTotpCode, setIsAuthenticatingToptCode] = useState(false)
  const isSubmitTotpCodeDisabled = totpCode.length === 6

  const handleOnChangeTotpCode = (event) => {
    if (event.target.value !== '') {
      setTotpCode(event.target.value)
    }
  }

  const handleTotpCodeFieldOnInput = (e) => {
    e.target.value = e.target.value.toString().slice(0, 6)
  }

  const handleSubmitToptCode = async () => {
    setIsAuthenticatingToptCode(true)
    const request = {
      requestBody: {
        userName: mfaData?.userName,
        sessionId: mfaData?.sessionId,
        accessCode: totpCode,
        challengeType: mfaData?.challengeType,
        challengeName: mfaData?.challengeName,
      },
    }
    await verifyMfaTokenForLogin(request)
      .then((response) => {
        const tokenData = {
          idToken: response.data?.data?.idToken,
          accessToken: response.data?.data?.accessToken,
          refreshToken: response.data?.data?.refreshToken,
        }
        const authenticatedUser = transformAuthenticatedUserData(response.data.data)
        onAuthenticationSuccess({ authenticatedUser, tokenData })
        return authenticatedUser
      })
      .then((authenticatedUser) => {
        /* Validate Tableau User. */
        setupTableauUser(authenticatedUser)
      })
      .catch((exception) => {
        setIsAuthenticatingToptCode(false)
        if (
          exception?.code ===
          errorCodes.userServiceErrorCodes.mfa.VERIFYMFATOKENLOGIN_USER_NOT_AUTHORIZED
        ) {
          const stateException = {
            code: exception.code,
            message: exception.message,
            status: exception.status,
          }
          navigate(routePaths.LOGIN, { state: { exception: stateException } })
        } else {
          dispatch(
            setSnackbar({
              message: exception?.message,
              severity: 'error',
              action: true,
            }),
          )
        }
      })
  }

  const handleTotpCodeFieldOnKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmitToptCode(e)
    }
    if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
      e.preventDefault()
    }
  }

  const getMFAMessage = () => {
    const challengeType = mfaData?.challengeType
    switch (challengeType) {
      case loginChallengeTypes.SMS_MFA_CODE:
        return Constants.enterMfa.ENTER_SMS_MFA_MESSAGE
      case loginChallengeTypes.SOFTWARE_TOKEN_MFA_CODE:
        return Constants.enterMfa.ENTER_APP_MFA_MESSAGE
      default:
        return null
    }
  }

  function onAuthenticationSuccess({ authenticatedUser, tokenData }) {
    /* Store Auth Tokens in Session storage */
    sessionStorage.setItem(sessionStorageKeyConstants.ID_TOKEN, tokenData.idToken)
    sessionStorage.setItem(sessionStorageKeyConstants.ACCESS_TOKEN, tokenData.accessToken)
    sessionStorage.setItem(sessionStorageKeyConstants.REFRESH_TOKEN, tokenData.refreshToken)
    /* Reset login attempts account when the user
            has successfully logged in.  */
    sessionStorage.setItem(sessionStorageKeyConstants.LOGIN_ATTEMPTS_COUNT, 0)
    /* Set User/App details in Local Storage */
    localStorage.setItem(localStorageKeyConstants.APP_VERSION, packageJson.version)
    localStorage.setItem(localStorageKeyConstants.STORED_USERNAME, authenticatedUser.email)
    localStorage.setItem(localStorageKeyConstants.CLIENT_CODE, authenticatedUser.clientCode)
    localStorage.setItem(localStorageKeyConstants.WAREHOUSE_CODE, authenticatedUser.whse_code)
    localStorage.setItem(localStorageKeyConstants.DMS_FLAG, authenticatedUser.dms)
    localStorage.setItem(localStorageKeyConstants.API_BASE_URL, import.meta.env.VITE_BASE_URL)
    dispatch(setAuthenticatedUser(authenticatedUser))
    /* When redux state is purged from local storage during logout,
       the persisted state only gets set again if the app is refreshed
       or opened in a new tab. If the user logout and login again, then
       need to run persist to start persisting again */
    persistor.persist()
  }

  function setupTableauUser(user) {
    /* Validate Tableau User. */
    const userEmail = user?.email
    if (userEmail) {
      getTableauCredentials(userEmail)
        .then((response) => {
          setIsAuthenticatingToptCode(false)
          sessionStorage.setItem(
            sessionStorageKeyConstants.TABLEAU_TOKEN,
            response?.data?.data?.token,
          )
          localStorage.setItem(
            localStorageKeyConstants.WMS_TABLEAU_DASHBOARD_URL,
            response?.data?.data?.dashboardURLs?.wmsDashboardURL,
          )
          localStorage.setItem(
            localStorageKeyConstants.DMS_TABLEAU_DASHBOARD_URL,
            response?.data?.data?.dashboardURLs?.dmsDashboardURL,
          )
          localStorage.setItem(
            localStorageKeyConstants.STATE_TABLEAU_DASHBOARD_URL,
            response?.data?.data?.dashboardURLs?.stateDashboardURL,
          )
          navigate('/dashboard')
        })
        .catch((exception) => {
          const errorCode = exception?.code
          if (errorCode === 'TABLEAU_USER_NOT_FOUND_EXCEPTION') {
            createTableauUser(userEmail, tableauUsers.type.Viewer)
              .then(() => {
                addTableauUserToGroup(userEmail, 'Client Account Managers')
                  .then(() => {
                    setIsAuthenticatingToptCode(false)
                    navigate('/dashboard')
                  })
                  .catch(() => {
                    setIsAuthenticatingToptCode(false)
                    navigate('/dashboard')
                  })
              })
              .catch(() => {
                navigate('/dashboard')
              })
          }
          /* bypassing - remove when done with logout testing */
          setIsAuthenticatingToptCode(false)
        })
    }
    setIsAuthenticatingToptCode(false)
  }

  const cardStyle = { height: '357px' }
  const cardContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  }
  const enterMfaMessageContainer = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '448px',
    marginTop: '12px',
  }
  const totpCodeTextFieldContainerStyle = { marginTop: '8px' }
  const totpCodeTextFieldStyle = {
    width: '240px',
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      display: 'none',
    },
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
  }
  const submitTotpCodeContainerStyle = { marginTop: '15px' }

  return (
    <>
      <Backdrop open={isAuthenticatingTotpCode}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Box sx={theme.soloContainer}>
        <Card sx={cardStyle} className='soloCard'>
          <CardContent sx={cardContentStyle}>
            <FormHeading formHeader={Constants.enterMfa.TITLE} />
            <Box sx={enterMfaMessageContainer}>
              <span>{getMFAMessage()}</span>
            </Box>
            <Box sx={totpCodeTextFieldContainerStyle}>
              <TextField
                sx={totpCodeTextFieldStyle}
                label={Constants.enableMfa.AUTHENTICATION_CODE}
                variant='outlined'
                type='number'
                slot={{ input: { maxLength: 6 } }}
                slotProps={{
                  input: { inputProps: { 'data-testid': 'enter-mfa-totp-code-input-field' } },
                }}
                onChange={handleOnChangeTotpCode}
                onInput={handleTotpCodeFieldOnInput}
                onKeyDown={handleTotpCodeFieldOnKeyDown}
              />
            </Box>
            <Box sx={submitTotpCodeContainerStyle}>
              <Button
                id='enter-mfa-submit-button'
                variant='contained'
                color='secondary'
                onClick={handleSubmitToptCode}
                disabled={!isSubmitTotpCodeDisabled}
              >
                {Constants.SUBMIT}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <GenericSnackbar />
    </>
  )
}

export default EnterMFA
